import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField as MuiTextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import Parse from 'parse';
import CustomPagination from '../../components/CustomPagination';
import CreateEditOperatorDialog from './dialog/CreateEditOperatorDialog';
import DeleteOperatorDialog from './dialog/DeleteOperatorDialog';

const OperatorList = () => {
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchBy, setSearchBy] = useState('name');
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const debounceRef = useRef(null);

  // Dialog states
  const [createEditDialogOpen, setCreateEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [dialogMode, setDialogMode] = useState('create');

  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Debounce search input
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchValue);
      setPage(1);
    }, 500);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchValue]);

  // Fetch operators from backend
  const fetchOperators = useCallback(async () => {
    setLoading(true);
    try {
      const result = await Parse.Cloud.run('getAllOperators', {
        limit: perPage,
        skip: (page - 1) * perPage,
        search: debouncedSearch || undefined
      });

      if (result.success) {
        setOperators(result.operators);
        setTotal(result.total);
      }
    } catch (error) {
      console.error('Error fetching operators:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to fetch operators',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [page, perPage, debouncedSearch]);

  useEffect(() => {
    fetchOperators();
  }, [fetchOperators]);

  // Export to Excel - fetch all data for export
  const handleExport = async () => {
    try {
      // Fetch all operators without pagination for export
      const result = await Parse.Cloud.run('getAllOperators', {
        limit: 10000,
        skip: 0,
        search: searchValue || undefined
      });

      if (!result.success || !result.operators || result.operators.length === 0) {
        setSnackbar({ open: true, message: 'No data to export', severity: 'warning' });
        return;
      }

      const exportData = result.operators.map((operator) => ({
        Name: operator.name,
        Username: operator.username,
        Email: operator.email,
        Phone: operator.phone || '',
        Role: operator.role,
        Domains: operator.domains?.map((d) => d.domainName).join(', ') || '',
        'Created At': new Date(operator.createdAt).toLocaleDateString()
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Operators');
      XLSX.writeFile(wb, `operators_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Export error:', error);
      setSnackbar({ open: true, message: 'Failed to export data', severity: 'error' });
    }
  };

  const handleCreateClick = () => {
    setSelectedOperator(null);
    setDialogMode('create');
    setCreateEditDialogOpen(true);
  };

  const handleEditClick = (operator) => {
    setSelectedOperator(operator);
    setDialogMode('edit');
    setCreateEditDialogOpen(true);
  };

  const handleDeleteClick = (operator) => {
    setSelectedOperator(operator);
    setDeleteDialogOpen(true);
  };

  const handleOperatorSaved = async () => {
    setSnackbar({
      open: true,
      message:
        dialogMode === 'create' ? 'Operator created successfully' : 'Operator updated successfully',
      severity: 'success'
    });
    fetchOperators();
  };

  const handleOperatorDeleted = async () => {
    setSnackbar({ open: true, message: 'Operator deleted successfully', severity: 'success' });
    fetchOperators();
  };

  const handleClearFilters = () => {
    setSearchValue('');
    setDebouncedSearch('');
    setSearchBy('name');
    setPage(1);
  };

  const handleSearch = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setDebouncedSearch(searchValue);
    setPage(1);
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Toolbar */}
      <Box
        sx={{
          backgroundColor: '#242424',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          p: 2,
          mb: 2
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          {/* Left side: Filters */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
              Filter By
            </Typography>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Search By</InputLabel>
              <Select
                value={searchBy}
                label="Search By"
                onChange={(e) => setSearchBy(e.target.value)}
                sx={{
                  backgroundColor: '#2B2B2B',
                  color: '#fff',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.5)'
                  },
                  '& .MuiSvgIcon-root': {
                    color: '#fff'
                  }
                }}
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="email">Email</MenuItem>
              </Select>
            </FormControl>

            <MuiTextField
              placeholder={`Enter ${searchBy}`}
              variant="outlined"
              size="small"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              sx={{
                minWidth: '200px',
                backgroundColor: '#2B2B2B',
                '& .MuiOutlinedInput-root': {
                  color: '#fff'
                }
              }}
            />

            <Button
              variant="contained"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
              sx={{
                backgroundColor: '#FFC107',
                color: '#000',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#FFD54F'
                }
              }}
            >
              Search
            </Button>

            <Button
              variant="outlined"
              onClick={handleClearFilters}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: '#fff',
                textTransform: 'uppercase',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)'
                }
              }}
            >
              Clear Filters
            </Button>
          </Box>

          {/* Right side: Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleExport}
              startIcon={<FileDownloadIcon />}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: '#fff',
                textTransform: 'uppercase'
              }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateClick}
              startIcon={<AddIcon />}
              sx={{
                backgroundColor: '#FFC107',
                color: '#000',
                textTransform: 'uppercase',
                '&:hover': {
                  backgroundColor: '#FFD54F'
                }
              }}
            >
              Create Operator
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Data Table */}
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          backgroundColor: '#242424',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
          <Box component="thead">
            <Box component="tr">
              {['Name', 'Username', 'Email', 'Phone', 'Domains', 'Created At', 'Actions'].map(
                (header) => (
                  <Box
                    component="th"
                    key={header}
                    sx={{
                      p: 2,
                      textAlign: header === 'Actions' ? 'center' : 'left',
                      color: '#FFC107',
                      fontWeight: 600,
                      borderBottom: '2px solid rgba(255, 255, 255, 0.1)'
                    }}
                  >
                    {header}
                  </Box>
                )
              )}
            </Box>
          </Box>
          <Box component="tbody">
            {loading ? (
              <Box component="tr">
                <Box component="td" colSpan={7} sx={{ p: 4, textAlign: 'center' }}>
                  <CircularProgress sx={{ color: '#FFC107' }} />
                </Box>
              </Box>
            ) : operators.length === 0 ? (
              <Box component="tr">
                <Box
                  component="td"
                  colSpan={7}
                  sx={{ p: 4, textAlign: 'center', color: 'rgba(255, 255, 255, 0.5)' }}
                >
                  No operators found
                </Box>
              </Box>
            ) : (
              operators.map((operator) => (
                <Box
                  component="tr"
                  key={operator.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)'
                    }
                  }}
                >
                  <Box
                    component="td"
                    sx={{
                      p: 2,
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    {operator.name}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: 2,
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    {operator.username}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: 2,
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    {operator.email}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: 2,
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    {operator.phone || '-'}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: 2,
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    {operator.domains?.length > 0
                      ? operator.domains.map((d) => d.domainName).join(', ')
                      : '-'}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: 2,
                      color: '#fff',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    {new Date(operator.createdAt).toLocaleString()}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    <IconButton
                      onClick={() => handleEditClick(operator)}
                      size="small"
                      sx={{ color: '#FFC107', mr: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteClick(operator)}
                      size="small"
                      sx={{ color: '#F44336' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))
            )}
          </Box>
        </Box>

        {/* Pagination */}
        <CustomPagination
          page={page}
          perPage={perPage}
          total={total}
          setPage={setPage}
          setPerPage={setPerPage}
        />
      </Box>

      {/* Dialogs */}
      <CreateEditOperatorDialog
        open={createEditDialogOpen}
        onClose={() => setCreateEditDialogOpen(false)}
        onSave={handleOperatorSaved}
        operator={selectedOperator}
        mode={dialogMode}
      />

      <DeleteOperatorDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDelete={handleOperatorDeleted}
        operator={selectedOperator}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OperatorList;
